import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Editor } from '@monaco-editor/react';
import { DocumentDuplicateIcon, MinusIcon, PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import InlineFeedback from '../components/InlineFeedback';
import { useFeedback } from '../hooks/useFeedback';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import { ConverterTargetType } from '../types';

type Mode = 'code' | 'file';

const TARGET_OPTIONS: { value: ConverterTargetType; label: string }[] = [
  { value: 'spark', label: 'PySpark' },
  { value: 'dbr', label: 'Databricks Notebook' },
];

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.sas', '.spk', '.egp'];
const ACCEPTED_FILE_TYPES = ALLOWED_EXTENSIONS.join(',');
const SUPPORTED_FORMATS_LABEL = ALLOWED_EXTENSIONS.join(', ');

const hasAllowedExtension = (filename: string) =>
  ALLOWED_EXTENSIONS.some((ext) => filename.toLowerCase().endsWith(ext));

const stripAllowedExtension = (filename: string) => {
  const matchedExt = ALLOWED_EXTENSIONS.find((ext) => filename.toLowerCase().endsWith(ext));
  return matchedExt ? filename.slice(0, -matchedExt.length) : filename;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const ConverterNewJob: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('code');
  const [code, setCode] = useState('');
  const [projectName, setProjectName] = useState('sas_code');
  const [hasCustomName, setHasCustomName] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fontSize, setFontSize] = useState<number>(14);
  const [editorHovered, setEditorHovered] = useState(false);
  const [targetType, setTargetType] = useState<ConverterTargetType>('spark');
  const feedback = useFeedback();
  const codeCopy = useCopyToClipboard();

  const increaseFontSize = () => setFontSize((prev) => Math.min(prev + 1, 24));
  const decreaseFontSize = () => setFontSize((prev) => Math.max(prev - 1, 10));

  const sanitizedProjectName = projectName.replace(/\s+/g, '_');

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === 'code') {
        return apiService.submitConverterJob({
          input_type: 'code',
          code,
          project_name: sanitizedProjectName,
          target_type: targetType,
        });
      }
      return apiService.submitConverterJob({
        input_type: 'file',
        file: file!,
        project_name: sanitizedProjectName,
        target_type: targetType,
      });
    },
    onSuccess: (response) => {
      navigate(`/converter/jobs/${response.job_id}`);
    },
    onError: (error: any) => {
      const status = error.response?.status;
      if (status === 413) {
        feedback.showError('Submission exceeds the 10 MB limit.');
        return;
      }
      if (status === 403) {
        feedback.showError('This instance does not have Converter access.');
        return;
      }
      feedback.showError(error.response?.data?.message || 'Failed to submit conversion job.');
    },
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = e.dataTransfer.files[0];
      handleFileSelected(dropped);
    }
  };

  const handleFileSelected = (selected: File) => {
    setFile(selected);
    if (!hasCustomName) {
      setProjectName(stripAllowedExtension(selected.name));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelected(e.target.files[0]);
    }
  };

  const codeBytes = useMemo(() => new Blob([code]).size, [code]);

  const validateAndSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    feedback.clear();

    if (mode === 'code') {
      if (!code.trim()) {
        feedback.showError('Enter some code to convert.');
        return;
      }
      if (codeBytes > MAX_BYTES) {
        feedback.showError('Code exceeds the 10 MB limit.');
        return;
      }
    } else {
      if (!file) {
        feedback.showError(`Select a file with one of these extensions: ${SUPPORTED_FORMATS_LABEL}.`);
        return;
      }
      if (!hasAllowedExtension(file.name)) {
        feedback.showError(`Use only ${SUPPORTED_FORMATS_LABEL} files.`);
        return;
      }
      if (file.size === 0) {
        feedback.showError('File is empty.');
        return;
      }
      if (file.size > MAX_BYTES) {
        feedback.showError('File exceeds the 10 MB limit.');
        return;
      }
    }

    mutation.mutate();
  };

  const isSubmitDisabled =
    mutation.isPending ||
    !sanitizedProjectName.trim() ||
    (mode === 'code' ? !code.trim() : !file);

  return (
    <div>
      <form onSubmit={validateAndSubmit} className="space-y-6" aria-label="New conversion">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">New Conversion</h2>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/converter')}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="btn-primary font-medium"
            >
              {mutation.isPending ? 'Submitting...' : 'Submit Job'}
            </button>
          </div>
        </div>

        <InlineFeedback feedback={feedback.feedback} />

        <div className="bg-widget-bg rounded-lg border border-secondary p-6">
          <div className="flex border-b border-secondary mb-6">
            <button
              type="button"
              onClick={() => {
                setMode('code');
                if (!hasCustomName) setProjectName('sas_code');
              }}
              className={`px-6 py-3 font-semibold transition-colors ${
                mode === 'code'
                  ? 'text-heading border-b-2 border-primary'
                  : 'text-secondary hover:text-heading'
              }`}
            >
              Code
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('file');
                if (!hasCustomName) {
                  setProjectName(file ? stripAllowedExtension(file.name) : '');
                }
              }}
              className={`px-6 py-3 font-semibold transition-colors ${
                mode === 'file'
                  ? 'text-heading border-b-2 border-primary'
                  : 'text-secondary hover:text-heading'
              }`}
            >
              File
            </button>
          </div>

          {mode === 'code' ? (
            <div className="space-y-4">
              <div>
                <span className="block text-sm font-medium text-secondary mb-2">
                  SAS Code
                </span>
                <div
                  className="overflow-visible relative"
                  style={{ height: 'max(20px, 30vh)' }}
                  onMouseEnter={() => setEditorHovered(true)}
                  onMouseLeave={() => setEditorHovered(false)}
                >
                  {editorHovered && (
                    <div className="absolute top-2 right-6 z-10 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={decreaseFontSize}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                        title="Decrease font size"
                      >
                        <MinusIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={increaseFontSize}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                        title="Increase font size"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                      {code && (
                        <button
                          type="button"
                          onClick={() => codeCopy.copy(code)}
                          className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                          title={codeCopy.status === 'copied' ? 'Copied!' : codeCopy.status === 'error' ? 'Failed to copy code' : 'Copy code'}
                        >
                          {codeCopy.status === 'copied' ? (
                            <CheckIcon className="w-4 h-4 text-primary" />
                          ) : codeCopy.status === 'error' ? (
                            <XMarkIcon className="w-4 h-4 text-red-400" />
                          ) : (
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  <Editor
                    height="100%"
                    defaultLanguage="sas"
                    value={code}
                    onChange={(value) => setCode(value || '')}
                    theme="vs-dark"
                    options={{
                      ariaLabel: 'SAS Code',
                      minimap: { enabled: false },
                      fontSize,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      automaticLayout: true,
                      padding: { top: 10, bottom: 10 },
                      contextmenu: false,
                    }}
                  />
                </div>
                <p className="text-xs text-secondary mt-1">
                  {formatFileSize(codeBytes)} / 10 MB
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="text-sm text-body-color flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>
                    <strong>Your privacy:</strong> We process your files securely. We store them so you can review the results. We delete them when you delete the job. We never share them.
                  </span>
                </p>
              </div>

              <div
                data-testid="drop-zone"
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-primary bg-primary/10' : 'border-secondary'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="converter-file-upload"
                  aria-label="Source file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept={ACCEPTED_FILE_TYPES}
                  multiple={false}
                />
                <label htmlFor="converter-file-upload" className="cursor-pointer">
                  {file ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-heading font-medium">1 file selected</p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFile(null);
                            const input = document.getElementById('converter-file-upload') as HTMLInputElement;
                            if (input) input.value = '';
                          }}
                          className="text-sm text-secondary hover:text-body-color transition-colors"
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-body-color truncate max-w-[70%]">{file.name}</span>
                          <span className="text-secondary">{formatFileSize(file.size)}</span>
                        </div>
                      </div>
                      <p className="text-secondary text-sm mt-2">
                        Total size: {formatFileSize(file.size)}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-body-color">Drag and drop a source file here, or click to browse.</p>
                      <p className="text-secondary text-sm mt-2">Supported file extensions: {SUPPORTED_FORMATS_LABEL}. Maximum file size: 10 MB.</p>
                    </div>
                  )}
                </label>
              </div>
            </>
          )}
        </div>

        <div className="bg-widget-bg rounded-lg border border-secondary p-6">
          <h3 className="text-lg font-semibold mb-4">Job Configuration</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="project-name" className="block text-sm font-medium text-secondary mb-2">Project Name</label>
              <input
                id="project-name"
                type="text"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  setHasCustomName(true);
                }}
                className="w-full bg-body-bg border border-secondary rounded-md px-3 py-2 text-body-color focus:outline-none focus:border-primary"
                placeholder="For example: Migration Q4"
                required
              />
              {projectName && projectName.includes(' ') && (
                <p className="text-xs text-secondary mt-1">
                  We replace spaces with underscores: <span className="text-primary">{sanitizedProjectName}</span>
                </p>
              )}
            </div>
            <div>
              <label htmlFor="target-type" className="block text-sm font-medium text-secondary mb-2">Target</label>
              <select
                id="target-type"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as ConverterTargetType)}
                className="w-full bg-body-bg border border-secondary rounded-md pl-3 pr-8 py-2 text-body-color focus:outline-none focus:border-primary select-chevron"
              >
                {TARGET_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

      </form>
    </div>
  );
};

export default ConverterNewJob;
